//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import licenseIcon from '../../icons/licenseDraft.svg';
import {
  licenseDisplayStyles,
  licenseColor,
} from '../../styles/LicenseDisplayStyles';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../ThemeContext';

interface LicenseInfo {
  tier: string;
  used: number;
  total: number;
  remaining: number;
  color: string;
}

interface LicenseTierData {
  selectedTier: string;
  tiers: LicenseInfo[];
}

interface LicenseDisplayProps {
  licenseData: LicenseTierData;
  hideTierNames?: boolean; // when true, do not render the tier label (e.g., Tier1/Tier2/Tier3)
}

const LicenseDisplay: React.FC<LicenseDisplayProps> = ({
  licenseData,
  hideTierNames = false,
}) => {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const styles = licenseDisplayStyles(darkMode);

  const renderTierItem = (tier: LicenseInfo, index: number) => {
    const isEnabled = tier.total > 0;
    const isSelected = licenseData.selectedTier === tier.tier && isEnabled;
    const usedCount = tier.used.toString().padStart(2, '0');
    const totalCount = tier.total.toString();

    const indicatorStyle = {
      ...(styles.tierIndicator as object),
      backgroundColor: isEnabled
        ? tier.color
        : darkMode
          ? licenseColor.disabledColorDark
          : licenseColor.disabledColor,
    } as SxProps<Theme>;

    const typographyStyle = {
      ...(styles.tierText as object),
      ...(isEnabled
        ? isSelected
          ? { color: tier.color }
          : (styles.tierTextDefault as object)
        : (styles.tierTextDisabled as object)),
      ...(isSelected ? (styles.selectedTier as object) : {}),
    } as SxProps<Theme>;

    const tierNameStyle = {
      color: isEnabled
        ? tier.color
        : darkMode
          ? licenseColor.disabledColorDark
          : licenseColor.disabledColor,
      fontWeight: isSelected ? 500 : 400,
    } as SxProps<Theme>;

    const countStyle = isEnabled ? (styles.tierCount as SxProps<Theme>) : {};

    return (
      <Box
        key={tier.tier}
        sx={
          {
            ...(styles.tierItem as object),
            ...(isEnabled ? {} : (styles.disabledTier as object)),
          } as SxProps<Theme>
        }
      >
        {index > 0 && <Box sx={styles.tierDivider as SxProps<Theme>} />}

        <Box sx={indicatorStyle} />

        <Typography variant="body2" sx={typographyStyle}>
          {hideTierNames ? null : (
            <>
              <Box component="span" sx={tierNameStyle}>
                {tier.tier}
              </Box>{' '}
            </>
          )}
          (
          <Box component="span" sx={countStyle}>
            {usedCount}
          </Box>{' '}
          of {totalCount})
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={styles.container as SxProps<Theme>}>
      <Box sx={styles.iconContainer as SxProps<Theme>}>
        <Box
          component="img"
          src={licenseIcon}
          sx={styles.icon as SxProps<Theme>}
        />
        <Typography variant="body2" sx={styles.labelText as SxProps<Theme>}>
          {t('licenses')}:
        </Typography>
      </Box>

      <Box sx={styles.tiersContainer as SxProps<Theme>}>
        {licenseData.tiers
          .filter((t) => (t?.tier || '').toLowerCase() !== 'none')
          .map(renderTierItem)}
      </Box>
    </Box>
  );
};

export default LicenseDisplay;
